import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UrlFormatter } from 'shared';
import { ParseService } from './parse.service';

/**
 * Native-Angular port of the AngularJS `Property` service
 * (account-v1/src/core/api/property.js). Verbatim: relative `/api/...` URLs via the
 * native `UrlFormatter.format`, Promise methods (`firstValueFrom` yields the body =
 * the v1 `$http…then(r => r.data)`), and `getCompanyProperties`' `parse.arrayOrDefault`
 * coercion via the native `ParseService`.
 *
 * WS5 #10 Stage A inc 2c: replaces the `$injector.get('Property')` bridge.
 * `providedIn:'root'` so consumers `inject()` it directly.
 */
@Injectable({ providedIn: 'root' })
export class PropertyApiService {
  private readonly http = inject(HttpClient);
  private readonly parse = inject(ParseService);

  getCompanyProperties(): Promise<any> {
    return firstValueFrom(this.http.get<any>('/api/properties?isShowAll=false')).then((data) => this.parse.arrayOrDefault(data));
  }

  overview(params: any): Promise<any> {
    return firstValueFrom(this.http.get<any>(UrlFormatter.format('/api/:type/:id/overview', params, true)));
  }

  create(companyUuid: string, name: string, url: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(UrlFormatter.format('/api/v2/:companyUuid/companies/:companyUuid/properties', { companyUuid }), { name, url }),
    );
  }

  getEmailsData(propertyUuid: string): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(UrlFormatter.format('/api/:propertyUuid/properties/:propertyUuid/communication/emails-data', { propertyUuid })),
    );
  }

  saveEmailsData(propertyUuid: string, model: any): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(UrlFormatter.format('/api/:propertyUuid/properties/:propertyUuid/communication/emails-data', { propertyUuid }), model),
    );
  }

  updateSettings(propertyUuid: string, settings: any): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(UrlFormatter.format('/api/:propertyUuid/properties/:propertyUuid/settings', { propertyUuid }), settings),
    );
  }

  getAdvocacyGuestJourney(propertyUuid: string): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(UrlFormatter.format('/api/v2/:propertyUuid/properties/:propertyUuid/advocacy/guestjourney', { propertyUuid })),
    );
  }
}
